import React, { useState, useEffect } from "react";
import CheckBox from "../workingCheckbox";
import "../../../css/multiSelectBubble.css";
import { Grid2, Typography } from "@mui/material";
import FormSelectInput from "./formSelectInput";
import FormTextArea from "./formTextArea";
import FormTextInput from "./formInput";
import FormMultipleSelectInput from "./formMultipleSelectInput";
import Button from "../../ui/button";

const FormField = ({
  label,
  title,
  id,
  value = [],
  overrideValue,
  onChange,
  type,
  options = [],
  placeholder,
  disabled,
  max,
  required,
}) => {
  const [customOptions, setCustomOptions] = useState(options);

  useEffect(() => {
    if (overrideValue && options.length > 0) {
      const selectedValue = options.find((opt) => opt.label === overrideValue) || value;
      handleChange(selectedValue);
    }
    if (JSON.stringify(customOptions) !== JSON.stringify(options)) setCustomOptions(options);
  }, [overrideValue, options]);

  const handleChange = (newValue) => onChange({ target: { id, value: newValue } });

  const renderInput = () => {
    const commonProps = { id, value, onChange: handleChange, options: customOptions, label, disabled, required };
    switch (type) {
      case "select":
        return <FormSelectInput {...commonProps} />;
      case "multi-select":
        return <FormMultipleSelectInput {...commonProps} />;
      case "select-with-add":
        return <SelectWithAddInput {...commonProps} setCustomOptions={setCustomOptions} />;
      case "textarea":
        commonProps.value = value.value;
        return <FormTextArea {...commonProps} placeholder={placeholder} onChange={(e) => handleChange({"value": e.target.value, "label": e.target.value})}/>;
      case "checkbox":
        commonProps.value = value.value
        return <CheckBox {...commonProps} accessor={id} callback={(accessor, value) => handleChange({"value": value, "label": value})} label={""}></CheckBox>   
      case "button":
        return <Button size="small" variant="outlined" max={max} onClick={(e) => onChange()}> {label}</Button>
      case "break":
        return <br/>
      default:
        commonProps.value = value.value
        return <FormTextInput {...commonProps} type={type} max={max} onChange={(e) => handleChange({"value": e.target.value, "label": e.target.value})} />;
    }
  };

  return (
    <Grid2 container spacing={2}>
      <Grid2 item size={4} sx={{display: 'flex', alignItems: 'center'}}>
      <Typography variant="h6" sx={{color: "var(--gray-600)"}}>
      {title || label}{required && <span style={{ color: "var(--red-600)" }}>*</span>}
      </Typography>
      </Grid2>
      <Grid2 item size={8} sx={{ padding: 0, margin: 0 }}>
      {renderInput()}
      </Grid2>
    </Grid2>
  );
};

const SelectInput = ({ id, value, onChange, options, label, disabled, required }) => (
  <select
    id={id}
    value={value?.value || ""}
    onChange={(e) => {
      const selectedOption = options.find(
        (opt) => String(opt.value) === e.target.value
      );
      onChange(selectedOption);
    }}
    disabled={disabled}
    required={required}
  >
    {value.value !== "" && (
      <option value="">{`Select ${label ? label.toLowerCase() : "option"}`}</option>
    )}

    {options.map((opt) => (
      <option key={opt.value} value={opt.value}>
        {opt.label}
      </option>
    ))}
  </select>
);

const MultiSelectInput = ({ id, value = [], onChange, options, label, disabled }) => {
  const filteredOptions = options.filter(
    (opt) => !Array.isArray(value) || !value.some((val) => String(val.value) === String(opt.value))
  );

  const handleMultiSelectChange = (selectedValue) => {
    const newSelection = options.find((opt) => String(opt.value) === String(selectedValue));

    if (!newSelection || Array.isArray(value) && value.some((val) => String(val.value) === String(selectedValue))) return;

    const updatedValues = [...value, newSelection];
    onChange(updatedValues);
  };

  const handleRemoveValue = (removeValue) => {
    const updatedValues = value.filter((val) => String(val.value) !== String(removeValue));
    onChange(updatedValues);
  };

  return (
    <div>
      <select
        id={id}
        value={""}
        onChange={(e) => handleMultiSelectChange(e.target.value)}
        disabled={disabled}
      >
        <option value="" disabled>{`Select ${label.toLowerCase()}`}</option>
        {filteredOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>

      <div className="multi-select-bubble-container">
        {Array.isArray(value) && value.map((val) => (
          <MultiSelectBubble
            key={val.value}
            value={val.value}
            label={val.label}
            onRemove={handleRemoveValue}
          />
        ))}
      </div>
    </div>
  );
};

const SelectWithAddInput = ({ id, value, onChange, options, label, disabled, required, setCustomOptions }) => (
  <div>
    <SelectInput
      id={id}
      value={value}
      onChange={onChange}
      options={options}
      label={label}
      disabled={disabled}
      required={required}
    />
    <button
      type="button"
      onClick={() => handleAddNewOption(options, setCustomOptions, onChange)}
    >
      Add New
    </button>
  </div>
);

const MultiSelectBubble = ({ value, label, onRemove }) => (
  <span className="multi-select-bubble">
    {label}
    <button
      type="button"
      className="multi-select-bubble-close"
      onClick={() => onRemove(value)}
    >
      ×
    </button>
  </span>
);

const handleAddNewOption = (options, setCustomOptions, onChange) => {
  const newOption = prompt("Enter new option:");
  if (newOption && !options.some((opt) => opt.label === newOption)) {
    const newEntry = { value: newOption, label: newOption };
    setCustomOptions((prev) => [...prev, newEntry]);
    onChange(newEntry); // New option is selected and added to value
  }
};

export default FormField;
